import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { StyledButton } from '../ui';

/**
 * JoinUsButton - Atomic component for partner section CTA buttons
 *
 * A specialized call-to-action button designed for the partner/collective
 * sections. Handles navigation and scroll-to-top behavior automatically.
 *
 * @param {Object} props - Component props
 * @param {Function} [props.onClick] - Additional click handler (navigation handled automatically)
 * @param {string} [props.text] - Button text content
 * @param {string} [props.route] - Navigation route
 * @param {'small'|'medium'|'large'} [props.size] - Button size variant
 * @param {Object} [props.sx] - Additional MUI sx styling props
 * @returns {JSX.Element} Rendered CTA button
 *
 * @example
 * // Basic usage with defaults
 * <JoinUsButton />
 *
 * @example
 * // Custom text and route
 * <JoinUsButton text="Become a Partner" route="/partnership" />
 *
 * @example
 * // With additional click handler
 * <JoinUsButton onClick={() => trackEvent('partner_cta_click')} />
 */
function JoinUsButton({
  onClick,
  text = 'Join Us',
  route = '/lets-collaborate',
  size = 'small',
  sx = {},
}) {
  const navigate = useNavigate();

  /**
   * Handle button click - navigate and scroll to top
   * @param {Event} event - Click event
   */
  const handleClick = (event) => {
    // Call custom onClick if provided
    if (onClick) {
      onClick(event);
    }

    // Navigate to route and scroll to top
    navigate(route);
    window.scrollTo(0, 0);
  };

  // Default button styling following brand guidelines
  const defaultStyles = {
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    px: 3,
    py: 1,
    fontSize: '0.9rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'var(--color-primary-dark)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },
  };

  return (
    <StyledButton
      variant="contained"
      size={size}
      onClick={handleClick}
      sx={{
        ...defaultStyles,
        ...sx,
      }}
    >
      {text}
    </StyledButton>
  );
}

JoinUsButton.propTypes = {
  /** Additional click handler */
  onClick: PropTypes.func,
  /** Button text content */
  text: PropTypes.string,
  /** Navigation route */
  route: PropTypes.string,
  /** Button size variant */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Additional MUI sx styling */
  sx: PropTypes.object,
};

export default JoinUsButton;
